import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import DropdownInput from "../../../components/DropdownInput";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface MonetixFormProps {
    broker: number,
    clientTransactionID?: number
}

const MonetixForm = ({broker, clientTransactionID}: MonetixFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [method, setMethod] = useState<string>();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [customerID, setCustomerID] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();
    const methods: FooterListItem[] = [
        {name: 'Cashmaal', code: 'cashmaal'},
    ]
    const allowedMethodData = [
        {code: 'cashmaal', currencies: ['PKR']},
    ];

    const clearData = () => {
        setInProgress(false);
        setMethod('');
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setAccountNumber('');
        setCustomerID('');
        setValidated(false)
    }

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
            if (response.data.request && response.data.request.method) {
                setMethod(response.data.request.method.toLowerCase());
            }
            if (response.data.request && response.data.request.nationalid) {
                setCustomerID(response.data.request.nationalid)
            }
            if (response.data.request && response.data.request.nationalid) {
                setCustomerID(response.data.request.nationalid)
            }
            if (response.data.transaction_data) {
                setAccountNumber(response.data.transaction_data.accountnumber)
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);


    const changeMethod = useCallback((s: string | number) => {
        clearData();
        setMethod(typeof s !== "string" ? s.toString() : s);
    }, [setMethod, clearData]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeCustomerID = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCustomerID(e.currentTarget.value)
        }
    }, [setValidated, setCustomerID]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                amount,
                method,
                email,
                broker,
                provider: WithdrawProvider.MONETIX,
                account_number: accountNumber,
                first_name: firstName,
                last_name: lastName,
                customer_id: customerID
            };
            API.makeMonetixWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    ErrorHandler(e, `post${APILink.MAKE_MONETIX_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, lastName, method, validated, amount, accountNumber, email, broker, customerID]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {
                key: 'method',
                label: t('admin.withdraws.payment_method'),
                type: InputType.select,
                options: methods.map((i) => {
                    return {name: i.name.toString(), id: i.code ?? ''}
                })
            },
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'customer_id', label: t('admin.withdraws.customer_id'), type: InputType.text, regex: '^.{10,}'},
            {key: 'account_number', label: t('admin.withdraws.account_number'), type: InputType.text, regex: '^.{10,}'},
        ];
        const data = {
            method,
            amount,
            customer_id: customerID,
            first_name: firstName,
            last_name: lastName,
            email,
            account_number: accountNumber
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, method, firstName, lastName, email, accountNumber, customerID, callback]);

    const customerIDUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.customer_id')} type="text" validated={validated} value={customerID}
                          fieldName="customer_id"
                          onChange={changeCustomerID}/>
        </div>
    }, [method, accountNumber, validated, customerID, changeCustomerID])

    const amountUI = useMemo(() => {
        if (!method)
            return <></>


        const allowedMethod = allowedMethodData.find(a => a.code === method);
        if (!allowedMethod || allowedMethod.currencies.length === 0)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: allowedMethod.currencies[0]})} type="number"
                          validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, method, changeAmount, amount]);

    const firstNameUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [method, accountNumber, validated, firstName, changeFirstName, customerID])

    const lastNameUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [method, validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [method, validated, email, changeEmail])

    const accountUI = useMemo(() => {
        if (!method)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [method, validated, accountNumber, changeAccountNumber])

    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        if (clientTransactionID) {
            getClientTransaction();
        }
        if (methods.length === 1) {
            // @ts-expect-error can be undefined
            setMethod(methods[0].code)
        }
    }, [broker, clientTransactionID]);

    return <>
        <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.payment_method')} className="w-full min-w-[200px]" selected={method}
                           onChange={changeMethod}
                           list={methods}/>
        </div>
        {customerIDUI}
        {accountUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(MonetixForm);
